import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../AuthProvider';
import { Users, RefreshCw, Shield, UserCheck, Eye, Edit, Plus, Trash2 } from 'lucide-react';
import AdminNav from './AdminNav';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const UserManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [appRoles, setAppRoles] = useState([]);

  // Grant Access tab: allow sign-in for emails outside allowed domains
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [grantAccessLoading, setGrantAccessLoading] = useState(false);
  const [newAllowedEmail, setNewAllowedEmail] = useState('');
  const [newAllowedEmailName, setNewAllowedEmailName] = useState('');
  const [newAllowedEmailPassword, setNewAllowedEmailPassword] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when token changes
  }, [token]);

  useEffect(() => {
    if (token && (location.pathname === '/admin/grant_access' || location.pathname === '/admin/users')) {
      fetchAllowedEmails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when token/path changes
  }, [token, location.pathname]);

  useEffect(() => {
    if (!token || location.pathname !== '/admin/users') return;
    axios.get(`${API_BASE}/admin/roles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAppRoles(res.data?.roles || []))
      .catch(() => setAppRoles([]));
  }, [token, location.pathname]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      // Ensure token is available
      if (!token) {
        setError('Authentication token not available. Please log in again.');
        setLoading(false);
        return;
      }
      // Explicitly set Authorization header as fallback
      console.log('[UserManagementPage] fetching GET /admin/users');
      const response = await axios.get(`${API_BASE}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const userList = response.data.users || [];
      setUsers(userList);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId) => {
    try {
      setSaving(true);
      if (!token) {
        alert('Authentication token not available. Please log in again.');
        return;
      }
      await axios.patch(
        `${API_BASE}/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingRole(null);
      setNewRole('');
      await fetchUsers();
    } catch (err) {
      console.error('Error updating role:', err);
      alert(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const fetchAllowedEmails = async () => {
    if (!token) return;
    try {
      setGrantAccessLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE}/admin/allowed-emails`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllowedEmails(response.data.emails || []);
    } catch (err) {
      console.error('Error fetching allowed emails:', err);
      setError(err.response?.data?.detail || 'Failed to load allowed emails.');
    } finally {
      setGrantAccessLoading(false);
    }
  };

  const handleAddAllowedEmail = async (e) => {
    e.preventDefault();
    const email = newAllowedEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    const display_name = newAllowedEmailName.trim() || '';
    const password = newAllowedEmailPassword.trim() || '';
    if (!display_name) {
      setError('Name is required.');
      return;
    }
    if (!password) {
      setError('Temporary password is required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    try {
      setAddingEmail(true);
      setError(null);
      await axios.post(
        `${API_BASE}/admin/allowed-emails`,
        { email, role: 'user', display_name: display_name || undefined, password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewAllowedEmail('');
      setNewAllowedEmailName('');
      setNewAllowedEmailPassword('');
      await fetchAllowedEmails();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add email.');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRemoveAllowedEmail = async (email) => {
    try {
      setRemovingEmail(email);
      await axios.delete(`${API_BASE}/admin/allowed-emails/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchAllowedEmails();
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove access.');
    } finally {
      setRemovingEmail(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'pm':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'reviewer':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'contributor':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4" />;
      case 'pm':
        return <UserCheck className="w-4 h-4" />;
      case 'reviewer':
        return <Eye className="w-4 h-4" />;
      case 'contributor':
        return <Edit className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getRoleDisplayName = (role) => {
    if (role === 'user') return 'user';
    const fromApi = appRoles.find((r) => r.code === role);
    if (fromApi?.display_name) return fromApi.display_name;
    return role ? role.charAt(0).toUpperCase() + role.slice(1) : role;
  };

  const adminActiveTab = location.pathname === '/admin/grant_access' ? 'grant_access' : 'users';

  if (loading && adminActiveTab === 'users') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-neutral-950">
        <div className="text-xl font-semibold">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 dark:bg-neutral-950 p-8">
      <div className="max-w-7xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col">
            <h1 className="text-3xl font-medium text-gray-900 dark:text-neutral-100">Admin Panel</h1>
            <p className="text-sm text-gray-500 dark:text-neutral-400 mt-2">
              Manage user roles and permissions (admin, pm, reviewer, contributor, creator)
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchUsers}
              className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-sm font-medium whitespace-nowrap border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button 
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-sm font-medium whitespace-nowrap border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
            >
              ← Back to App
            </button>
          </div>
        </div>

        <AdminNav />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {adminActiveTab === 'grant_access' ? (
          <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-md border border-gray-200 dark:border-neutral-800 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-neutral-700">
              <p className="text-sm text-gray-500 dark:text-neutral-400 mb-2">
                Grant sign-in access for invitees (non–@cultureflipper.com). All fields are required.
              </p>
              <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 mb-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Temporary password is shown only once</strong> and is not stored or displayed anywhere after you click Add. You must remember it and share it with the invitee manually (e.g. in person or via a secure channel).
                </p>
              </div>
              <form onSubmit={handleAddAllowedEmail} className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px] flex-1">
                  <label htmlFor="grant-email" className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Email <span className="text-red-500">*</span></label>
                  <input
                    id="grant-email"
                    type="email"
                    value={newAllowedEmail}
                    onChange={(e) => setNewAllowedEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={addingEmail}
                    required
                  />
                </div>
                <div className="min-w-[140px] flex-1">
                  <label htmlFor="grant-name" className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Name <span className="text-red-500">*</span></label>
                  <input
                    id="grant-name"
                    type="text"
                    value={newAllowedEmailName}
                    onChange={(e) => setNewAllowedEmailName(e.target.value)}
                    placeholder="Display name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={addingEmail}
                    required
                  />
                </div>
                <div className="min-w-[180px] flex-1">
                  <label htmlFor="grant-password" className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Temporary password <span className="text-red-500">*</span></label>
                  <input
                    id="grant-password"
                    type="password"
                    value={newAllowedEmailPassword}
                    onChange={(e) => setNewAllowedEmailPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={addingEmail}
                    minLength={8}
                    required
                    autoComplete="off"
                  />
                </div>
                <button
                  type="submit"
                  disabled={addingEmail}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-md transition-colors shrink-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {addingEmail ? 'Adding...' : 'Add'}
                </button>
              </form>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-neutral-700">
              {grantAccessLoading ? (
                <div className="px-6 py-8 text-center text-gray-500 dark:text-neutral-400">Loading...</div>
              ) : allowedEmails.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500 dark:text-neutral-400">
                  No granted emails yet. Add one above to allow sign-in for someone outside the allowed domains.
                </div>
              ) : (
                allowedEmails.map((row) => (
                  <div key={row.email} className="px-6 py-4 flex items-center gap-3 flex-wrap">
                    <span className="text-gray-900 dark:text-neutral-100 font-medium">{row.email}</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                      {getRoleDisplayName(row.role || 'user')}
                    </span>
                    {row.created_at && (
                      <span className="text-xs text-gray-500 dark:text-neutral-400">
                        added {new Date(row.created_at).toLocaleDateString()}
                        {row.created_by && ` by ${row.created_by}`}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
        <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-md border border-gray-200 dark:border-neutral-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Projects</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Tasks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Added</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-neutral-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-neutral-100">
                          {user.name || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-neutral-100">
                          {user.email || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingRole === user.id ? (
                          <div className="space-y-2">
                            <select
                              value={newRole}
                              onChange={(e) => setNewRole(e.target.value)}
                              className="text-sm border border-gray-300 dark:border-neutral-700 rounded-md px-2 py-1 bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100"
                            >
                              {(appRoles.length ? appRoles : [
                                { code: 'user', display_name: 'user' },
                                { code: 'contributor', display_name: 'Contributor' },
                                { code: 'reviewer', display_name: 'Reviewer' },
                                { code: 'pm', display_name: 'PM' },
                                { code: 'admin', display_name: 'Admin' },
                              ]).map((r) => (
                                <option key={r.code} value={r.code}>{r.display_name || r.code}</option>
                              ))}
                            </select>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleUpdateRole(user.id)}
                                disabled={saving}
                                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors disabled:opacity-50"
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingRole(null);
                                  setNewRole('');
                                }}
                                className="text-xs bg-gray-200 hover:bg-gray-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-gray-700 dark:text-neutral-200 px-3 py-1 rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                            {getRoleIcon(user.role)}
                            <span className="ml-1.5">{getRoleDisplayName(user.role)}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-neutral-100">
                        {user.project_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-neutral-100">
                        {user.task_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-neutral-400">
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-neutral-400">
                        {user.granted_at != null || user.granted_by
                          ? (() => {
                              const dateStr = user.granted_at ? new Date(user.granted_at).toLocaleDateString() : '';
                              const by = user.granted_by ? (user.granted_by.includes('@') ? user.granted_by.split('@')[0] : user.granted_by) : '';
                              return `on ${dateStr || '—'}${by ? ` by ${by}` : ''}`.trim() || '—';
                            })()
                          : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => {
                              setEditingRole(user.id);
                              setNewRole(user.role);
                            }}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 flex items-center"
                            title="Edit Role"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                          </button>
                          {allowedEmails.some((e) => (e.email || '').toLowerCase() === (user.email || '').toLowerCase()) && (
                            <button
                              onClick={() => handleRemoveAllowedEmail(user.email)}
                              disabled={removingEmail === user.email}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 flex items-center disabled:opacity-50"
                              title="Remove access"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;
